/**
 * Static content, types, and routing logic for the
 * "Appoint an Officer" workflow.
 *
 * Language canon for this folder: plain spoken register, short sentences, no
 * dashes, no ministry vocabulary, no internal codes, and true dates only.
 */

export type YesNo = 'yes' | 'no';

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  helper: string;
}

export const ORIENTATION_KNOW =
  'Offices are appointed and accepted: two signatures, one appointment. Office and banking authority are separate grants, each on its own resolution, and acceptance means the person read what the office carries before signing.';

export const ORIENTATION_DO =
  'Name the person and the office. The portal prepares the appointment, the acceptance, and the banking grant only if you choose it.';

export const ORIENTATION_SCHEDULE =
  'Nothing recurring comes from an appointment beyond what the office itself already carries in your governance calendar.';

export const MEMBER_QUESTION = 'Is the person already a Member?';

export const NOT_A_MEMBER_STOP =
  'Admission comes first. Use the Add a UNA Member workflow to bring the person into Membership, then come back and appoint them. No papers prepare here until they are a Member.';

export const SPOUSE_QUESTION = 'Is the person your spouse?';

export const SPOUSE_POSTURE_TEACHING =
  'A spouse can serve at three depths, chosen deliberately. Posture one, member only: no office. Posture two, officer without banking authority: governance standing, no account access. Posture three, full co officer: equal office, equal signing, equal banking, each grant by its own resolution. A spouse installed at posture three cannot, as a practical matter, be removed without their cooperation. That is its strength for a united family and its gravity for everyone: choose it the way you chose the marriage. Anyone with a prenuptial agreement or complicated marital circumstances sees their counsel before choosing.';

export const OFFICE_HELPER =
  'The two standard offices are Principal Executive Officer and Secretary Treasurer. The Principal Executive Officer remains one person.';

export const BANKING_DECLINED_LINE =
  'Banking authority can always be granted later by its own resolution. Office does not carry it automatically, and that separation protects everyone.';

export const CONDITIONAL_RESIGNATION_LINE =
  'A conditional resignation, the instrument some families pair with an appointment, is under legal review and prepares with the team rather than here.';

export const SEQUENCING_TEACHING =
  'Papers carry the true date they are signed. There is no back dating here, and the portal will not offer it.';

export const YES_NO_OPTIONS: ChoiceOption<YesNo>[] = [
  { value: 'yes', label: 'Yes', helper: '' },
  { value: 'no', label: 'No', helper: '' },
];

export interface AppointWorkflowData {
  is_member?: YesNo;
  is_spouse?: YesNo;
  person_name?: string;
  office?: string;
  effective_date?: string;
  grant_banking?: YesNo;
  banking_accounts?: string;
  notes?: string;
}

export interface AppointRouteResult {
  generatesNothing: boolean;
  hardStop: boolean;
  summary: string;
}

export function routeAppointment(data: AppointWorkflowData): AppointRouteResult {
  if (data.is_member === 'no') {
    return { generatesNothing: true, hardStop: true, summary: 'Bring the person into Membership first.' };
  }
  if (data.is_member !== 'yes') {
    return { generatesNothing: true, hardStop: false, summary: 'Answer whether the person is already a Member.' };
  }
  if (data.grant_banking === 'yes') {
    return {
      generatesNothing: false,
      hardStop: false,
      summary: 'Three papers prepare: the appointment resolution, the officer acceptance, and the banking authority resolution.',
    };
  }
  return {
    generatesNothing: false,
    hardStop: false,
    summary: 'Two papers prepare: the appointment resolution and the officer acceptance.',
  };
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
